import Task from "./Task";
import { useEffect, useState } from "react";
import { ref, set, onValue, off } from "firebase/database";
import {
  GridItem,
  VStack,
  Text,
  FormControl,
  FormErrorMessage,
  Input,
  Button,
  HStack,
  Box,
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function TaskList({ sessionId, user, uid, db }) {
  const [input, setInput] = useState("");
  const [tasks, setTasks] = useState(null);
  const [nextIndex, setNextIndex] = useState(null);
  const [numTasks, setNumTasks] = useState(null);
  const [numCompletedTasks, setNumCompletedTasks] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    const tasksRef = ref(db, "sessions/" + sessionId + "/tasks/" + uid);
    onValue(tasksRef, (snapshot) => {
      if (snapshot.exists()) {
        setTasks(snapshot.val());
        setNumTasks(Object.keys(snapshot.val()).length);
        setNumCompletedTasks(
          Object.values(snapshot.val()).reduce((acc, value) => {
            return acc + value["done"];
          }, 0)
        );
        setNextIndex(
          parseInt(
            Object.keys(snapshot.val())[Object.keys(snapshot.val()).length - 1]
          ) + 1
        );
      } else {
        setTasks({});
        setNumTasks(0);
        setNumCompletedTasks(0);
        setNextIndex(0);
      }
    });

    return () => {
      off(tasksRef);
    };
  }, [db, sessionId, uid]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === "") {
      setIsInvalid(true);
    } else {
      tasks[nextIndex] = { name: input, done: false };
      setTasks(tasks);
      setInput("");
      setNumTasks(numTasks + 1);
      setNextIndex(nextIndex + 1);
      set(ref(db, "sessions/" + sessionId + "/tasks/" + uid), tasks);
    }
  };

  const updateNumCompletedTasks = (id, val, taskName) => {
    if (val) {
      setNumCompletedTasks(numCompletedTasks + 1);
    } else {
      setNumCompletedTasks(numCompletedTasks - 1);
    }

    tasks[id]["done"] = val;
    setTasks(tasks);
    set(ref(db, "sessions/" + sessionId + "/tasks/" + uid), tasks);
  };

  const deleteTask = (id) => {
    if (tasks[id]["done"]) {
      setNumCompletedTasks(numCompletedTasks - 1);
    }
    setNumTasks(numTasks - 1);
    delete tasks[id];
    setTasks(tasks);
    set(ref(db, "sessions/" + sessionId + "/tasks/" + uid), tasks);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (e.target.value !== "") {
      setIsInvalid(false);
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }
    if (destination.index === source.index) {
      return;
    }
    const newTasks = Array.from(tasks);
    newTasks.splice(source.index, 1);
    newTasks.splice(destination.index, 0, tasks[draggableId]);
    setTasks(newTasks);
    set(ref(db, "sessions/" + sessionId + "/tasks/" + uid), newTasks);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <GridItem key={uid} borderRadius={10} border="1px" borderColor="gray.400">
        {!tasks ? null : (
          <VStack w="full" h="full" p={4} justifyContent="space-between">
            <VStack w="full">
              <Text fontSize="2xl">{user.displayName}</Text>
              {numTasks > 0 ? (
                <Text fontWeight="bold">
                  {Math.round((numCompletedTasks / numTasks) * 100)}% Completed
                </Text>
              ) : null}
              <Box py={2} w="full">
                <Droppable droppableId={uid}>
                  {(provided) => (
                    <VStack
                      w="full"
                      h="200px"
                      overflowY="auto"
                      border="1px"
                      borderColor="gray.200"
                      py={2}
                      borderRadius={4}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {Object.entries(tasks).map(([key, value], index) => (
                        <Draggable draggableId={key} index={index} key={key}>
                          {(provided) => (
                            <Box
                              w="full"
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                            >
                              <Task
                                id={key}
                                name={value.name}
                                done={value.done}
                                updateParent={updateNumCompletedTasks}
                                isTask={true}
                                deleteTask={deleteTask}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </VStack>
                  )}
                </Droppable>
              </Box>
            </VStack>

            <form onSubmit={handleSubmit}>
              <FormControl isInvalid={isInvalid}>
                <HStack>
                  <Input
                    type="text"
                    placeholder="Add a task"
                    value={input}
                    onChange={handleInputChange}
                    required
                  />
                  <Button colorScheme="blue" type="submit">
                    Add
                  </Button>
                </HStack>
                <FormErrorMessage>Task name is required</FormErrorMessage>
              </FormControl>
            </form>
          </VStack>
        )}
      </GridItem>
    </DragDropContext>
  );
}

export default TaskList;
